import { getThemeColor, type MantineTheme } from "@mantine/core";
import type { OverallSeverity } from "@components/overview_pages/utils/overallStatus";
import type { PhoriaQualityToken } from "@components/overview_pages/utils/phoriaCalculations";
import type { RiskLevel } from "@components/overview_pages/utils/severity";
import type { ClinicalRiskLevel } from "@components/testContainer/result/utils/clinicalSummaryTypes";
import type { BandStatus } from "@utils/cognitiveProfile";

export const STATUS_COLOR = {
  good: "green",
  cautionYellow: "yellow",
  cautionOrange: "orange",
  bad: "red",
  info: "blue",
  unavailable: "gray",
} as const;

export const RISK_LEVEL_COLOR: Record<RiskLevel, string> = {
  low: STATUS_COLOR.good,
  moderate: STATUS_COLOR.cautionOrange,
  high: STATUS_COLOR.bad,
};

export const OVERALL_SEVERITY_COLOR: Record<OverallSeverity, string> = {
  normal: STATUS_COLOR.good,
  monitor: STATUS_COLOR.cautionYellow,
  attention: STATUS_COLOR.bad,
};

export const LAZY_EYE_HEADER_COLOR: Record<OverallSeverity, string> = {
  normal: STATUS_COLOR.good,
  monitor: STATUS_COLOR.cautionOrange,
  attention: STATUS_COLOR.bad,
};

export const CLINICAL_RISK_COLOR: Record<ClinicalRiskLevel, string> = {
  low: STATUS_COLOR.good,
  monitor: STATUS_COLOR.cautionYellow,
  high: STATUS_COLOR.bad,
};

export type ConcussionRiskLevel = "expected" | "borderline" | "monitor" | "unavailable";

export const CONCUSSION_RISK_COLOR: Record<ConcussionRiskLevel, string> = {
  expected: STATUS_COLOR.good,
  borderline: STATUS_COLOR.cautionYellow,
  monitor: STATUS_COLOR.bad,
  unavailable: STATUS_COLOR.unavailable,
};

export const COGNITIVE_BAND_COLOR: Record<BandStatus, string> = {
  excellent: STATUS_COLOR.good,
  typical: STATUS_COLOR.info,
  // Orange is the closest "warning" tone in the restricted blue/green/orange/grey/yellow palette.
  needsAttention: STATUS_COLOR.cautionOrange,
};

export const PHORIA_QUALITY_COLOR: Record<PhoriaQualityToken, string> = {
  good: STATUS_COLOR.good,
  near_orthophoria: STATUS_COLOR.cautionYellow,
  no_strong_tendency: STATUS_COLOR.unavailable,
  caution: STATUS_COLOR.bad,
  unknown: STATUS_COLOR.unavailable,
};

export type EyeSide = "left" | "right";

const EYE_SERIES_BASE: Record<EyeSide, string> = {
  left: STATUS_COLOR.info,
  right: STATUS_COLOR.good,
};

export const DEFAULT_SERIES_SHADE = 6;

export function eyeSeriesColor(eye: EyeSide, shade: number = DEFAULT_SERIES_SHADE): string {
  return `${EYE_SERIES_BASE[eye]}.${shade}`;
}

export function resolveEyeSeriesColor(theme: MantineTheme, eye: EyeSide, shade?: number): string {
  return getThemeColor(eyeSeriesColor(eye, shade), theme);
}

export const CHART_ACCENT = {
  target: "red.6",
  expected: "gray.5",
  stimulus: "gray.6",
  npcBreak: "violet.7",
  npcRecovery: "orange.6",
  grid: "gray.2",
  tick: "gray.6",
  vaThresholdRow: "blue.6",
  vaPassedRow: "green.6",
} as const;

export function resolveChartAccent(theme: MantineTheme, key: keyof typeof CHART_ACCENT): string {
  return getThemeColor(CHART_ACCENT[key], theme);
}
